import { Headers, queryClient, tryToRefreshToken } from "@data/lib";
import { useAuthStore } from "@mod/auth";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { fetchToApi } from "./lib/fetch-to-api";
import { HttpMethod, QueryParams, ResponseType } from "./types";

/**
 * Custom hook to handle API calls with authentication and error handling.
 *
 * @param queryKey - A unique key for the query.
 * @returns An object containing methods for making API calls.
 *
 * @template T - The expected response type.
 *
 * @function fetchApi
 * @description Makes an API call with the given parameters and handles authentication errors.
 * @param url - The endpoint URL.
 * @param method - The HTTP method (default is "GET").
 * @param payload - The request payload.
 * @returns A promise that resolves to the response data or null.
 *
 * @function useSecuredApiCall
 * @description A hook to make a secured API call using React Query.
 * @param params - An object containing the URL, method, payload, keys, and enabled flag.
 * @returns The result of the useQuery hook.
 *
 * @function useApiMutation
 * @description A hook to make a mutation API call using React Query.
 * @param params - An object containing the URL, method, keys, and onSuccess callback.
 * @returns The result of the useMutation hook.
 *
 * @property user - The authenticated user object.
 */
export const useApi = (queryKey: string) => {
  const { user, token, refreshToken } = useAuthStore();
  const navigate = useNavigate();
  const logOutAction = useAuthStore.use.logout();
  const loginAction = useAuthStore.use.login();

  /**
   * Fetches data from the API with the specified URL, HTTP method, and payload.
   * Handles various HTTP response statuses and attempts to refresh the token if necessary.
   *
   * @template T - The type of the response data.
   * @param {string} url - The URL to fetch data from.
   * @param {HttpMethod} [method] - The HTTP method to use (default is "GET").
   * @param {any} [payload] - The payload to send with the request.
   * @returns {Promise<ResponseType<T> | null>} - A promise that resolves to the response data or null.
   */
  const fetchApi = async <T>(
    url: string,
    method?: HttpMethod,
    payload?: any,
    headers?: Headers
  ): Promise<ResponseType<T> | null> => {
    const retry = async () => {
      const response = await fetchToApi(url, method ?? "GET", payload, token, headers);

      if (!response.ok) {
        switch (response.status) {
          case 401:
          case 403:
            return tryToRefreshToken(
              refreshToken ?? "",
              () => {
                console.log("forbidden");
                logOutAction();
                navigate("/login");
              },
              (token, refreshToken) => {
                queryClient.clear();
                loginAction(token, refreshToken);
              }
            );
          case 404:
            console.log("not found");
            break;
          case 500:
            console.log("internal server error");
            break;
          default:
            console.log("unknown error");
            break;
        }
      }

      return await response.json();
    };

    return await retry();
  };

  /**
   * Custom hook to make a secured API call using React Query.
   *
   * @template T - The type of the response data.
   * @param {Object} params - The parameters for the API call.
   * @param {string} params.url - The URL of the API endpoint.
   * @param {string} params.method - The HTTP method to use for the API call.
   * @param {Object} params.payload - The payload to send with the API call.
   * @param {Object} params.keys - The keys to use for the query.
   * @param {boolean} [params.enabled=true] - Whether the query should be enabled.
   * @returns {UseQueryResult<ResponseType<T> | null>} The result of the query.
   */
  const useSecuredApiCall = <T>({ url, method, payload, keys, enabled = true }: QueryParams) => {
    return useQuery({
      queryKey: [queryKey, { ...keys }],
      queryFn: async (): Promise<ResponseType<T> | null> => {
        const response = await fetchApi<T>(url, method, payload);
        return response;
      },
      enabled,
      placeholderData: keepPreviousData,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: true,
      gcTime: 1000 * 60 * 60,
      staleTime: 1000 * 60 * 60
    });
  };

  /**
   * Custom hook to perform a mutation using the provided API endpoint.
   *
   * @template T - The expected response type from the API.
   * @param {Object} params - The parameters for the mutation.
   * @param {string} params.url - The API endpoint URL.
   * @param {string} params.method - The HTTP method to use for the request (e.g., 'POST', 'PUT').
   * @param {Array<any>} params.keys - The keys to be used for the mutation query.
   * @param {Function} params.onSuccess - Callback function to be executed on successful mutation.
   * @returns {UseMutationResult<T, unknown, any, unknown>} - The result of the mutation hook.
   */
  const useApiMutation = <T>({ url, method, keys, onSuccess, headers }: QueryParams) =>
    useMutation({
      mutationKey: [queryKey, keys],
      mutationFn: async (payload: any) => {
        return await fetchApi<T>(url, method, payload, headers);
      },
      onSuccess
    });

  return { fetchApi, useApiMutation, useSecuredApiCall, user };
};
